import { Injectable, inject, type ViewContainerRef } from '@angular/core';
import type { AlbumEditorUploadedPhoto, AlbumEditorUploadQueuePort } from '@lite-workspace/album-editor/ports';
import {
  PhotoUploadCoordinatorService,
  type UploadJob,
} from '../features/projects/domain/application/photo-upload-coordinator.service';
import { UploadIntakeFlowService } from '../features/projects/domain/application/upload-intake-flow.service';
import type { Photo } from '../features/projects/domain/entities/project.models';

const POLL_INTERVAL_MS = 300;
// 'unknown' is a job mid-reconciliation after a missed socket event — still in flight: dropping it
// here ends the drain early and the editor keeps a preview blob that is about to be revoked.
const IN_FLIGHT_STATUSES: readonly string[] = ['waiting_for_project', 'queued', 'uploading', 'processing', 'unknown'];

/** Placement math needs the ORIGINAL's dimensions; the reveal event carries the thumb's, so the
 *  size measured off the picked file at intake wins whenever it was readable. */
function toUploadedPhoto(job: UploadJob, photo: Photo): AlbumEditorUploadedPhoto {
  // The reveal builds the photo without server tags; the job's own fileTags (`retouched`) are what the
  // server unions onto the row, so the editor marks a retouched rewrite without waiting for a re-read.
  const tags = job.fileTags?.length ? [...new Set([...(photo.tags ?? []), ...job.fileTags])] : photo.tags;
  return {
    id: photo.id,
    name: photo.name ?? '',
    thumbUrl: photo.thumbUrl,
    smallUrl: photo.smallUrl,
    mediumUrl: photo.mediumUrl,
    originalUrl: photo.originalUrl,
    width: job.previewWidth ?? photo.width ?? null,
    height: job.previewHeight ?? photo.height ?? null,
    replaceOf: job.replaceOf,
    ...(tags ? { tags } : {}),
  };
}

/** The picked file itself, under the id the server will give it — the panel shows it while the slicing runs. */
function toPreviewPhoto(job: UploadJob): AlbumEditorUploadedPhoto | null {
  if (!job.previewUrl) return null;
  return {
    id: job.clientId,
    name: job.name,
    thumbUrl: job.previewUrl,
    smallUrl: job.previewUrl,
    mediumUrl: job.previewUrl,
    originalUrl: job.previewUrl,
    width: job.previewWidth,
    height: job.previewHeight,
    replaceOf: job.replaceOf,
    preview: true,
  };
}

/** The editor's upload path — the SAME intake pause as the Photos page (replace confirmation,
 *  check-failed stop), then a drain scoped to this batch's jobs only.
 *  Root-provided: the root-scoped AlbumEditorStore resolves the port from the root injector. */
@Injectable({ providedIn: 'root' })
export class EditorUploadQueuePort implements AlbumEditorUploadQueuePort {
  private readonly coordinator = inject(PhotoUploadCoordinatorService);
  private readonly intakeFlow = inject(UploadIntakeFlowService);
  /** Last picture handed to the editor per job — the local file first, the server's own version after. */
  private readonly emittedUrls = new Map<string, string>();

  async uploadPhotos(
    vcr: ViewContainerRef,
    projectId: string,
    files: readonly File[],
    onPhotoReady: (photo: AlbumEditorUploadedPhoto) => void,
    options?: { readonly preferredTargetIds?: readonly string[]; readonly retouch?: boolean; readonly targetId?: string },
  ): Promise<{ refused: number }> {
    const { retouch, ...rest } = options ?? {};
    const outcome = await this.intakeFlow.run(vcr, projectId, files, {
      ...rest,
      ...(retouch ? { intent: 'retouch' as const } : {}),
    });
    if (!outcome.committed || !outcome.summary) return { refused: 0 };
    const { summary } = outcome;
    await this.drain(projectId, summary.uploadBatchId, onPhotoReady);
    // A lost batch mint is already toasted by the intake — counts as reported, so the host adds no second line.
    const refused = summary.rejected.length + (summary.skippedAmbiguous?.length ?? 0) + (summary.batchMintFailed ? 1 : 0);
    return { refused };
  }

  /** Watches ONLY this batch's jobs — a parallel upload from the Photos page never leaks into the panel.
   *  A null batch key (intake flag OFF) matches every un-batched job — today's coarse project-wide drain. */
  private async drain(
    projectId: string,
    batchId: string | null,
    onPhotoReady: (photo: AlbumEditorUploadedPhoto) => void,
  ): Promise<void> {
    const jobs = this.coordinator.jobsForProject(projectId);
    for (;;) {
      const batch = jobs().filter(job => job.batchId === batchId);
      this.emitPhotos(batch, onPhotoReady);
      if (!batch.some(job => IN_FLIGHT_STATUSES.includes(job.status))) return;
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
    }
  }

  private emitPhotos(jobs: readonly UploadJob[], onPhotoReady: (photo: AlbumEditorUploadedPhoto) => void): void {
    for (const job of jobs) {
      if (job.file === null || job.status === 'duplicate') continue;
      const photo = job.status === 'ready' && job.photo ? toUploadedPhoto(job, job.photo) : toPreviewPhoto(job);
      if (!photo?.thumbUrl) continue;
      const urls = `${photo.thumbUrl}|${photo.smallUrl ?? ''}|${photo.mediumUrl ?? ''}|${photo.originalUrl ?? ''}`;
      if (this.emittedUrls.get(job.clientId) === urls) continue;
      this.emittedUrls.set(job.clientId, urls);
      onPhotoReady(photo);
    }
  }
}
